import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { wholesaleOrders } from '../../services/api';
import { generateInvoicePdf } from '../../utils/generateInvoicePdf';
import { ClipboardList, Package, RotateCcw, Download } from 'lucide-react';
import ReorderModal from '../../components/ReorderModal';

const fmt = (n) =>
  '$' + parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_BADGE = {
  pending:   'bg-warning text-dark',
  confirmed: 'bg-info text-dark',
  shipped:   'bg-primary',
  delivered: 'bg-success',
  invoiced:  'bg-secondary',
  paid:      'bg-dark',
  cancelled: 'bg-danger',
};

const OPEN_STATUSES      = ['pending', 'confirmed', 'shipped', 'invoiced'];
const COMPLETED_STATUSES = ['delivered', 'paid', 'cancelled'];

export default function OrderHistory() {
  const [orders,         setOrders]         = useState([]);
  const [tab,            setTab]            = useState('open');
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');
  const [reorderOrderId, setReorderOrderId] = useState(null);
  const [dlLoading,      setDlLoading]      = useState(null); // orderID being downloaded
  const [toast,          setToast]          = useState('');

  const handleDownload = async (orderId) => {
    setDlLoading(orderId);
    try {
      const res = await wholesaleOrders.getInvoice(orderId);
      await generateInvoicePdf(res.data.data);
    } catch {
      // silently fail
    } finally {
      setDlLoading(null);
    }
  };

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  useEffect(() => {
    wholesaleOrders.list()
      .then(res => setOrders(res.data.orders || []))
      .catch(() => setError('Failed to load orders.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  const openOrders      = orders.filter(o => OPEN_STATUSES.includes(o.orderStatus));
  const completedOrders = orders.filter(o => COMPLETED_STATUSES.includes(o.orderStatus));
  const displayed       = tab === 'open' ? openOrders : completedOrders;

  const renderTable = (rows, emptyMsg) => {
    if (rows.length === 0) {
      return (
        <div className="text-center text-muted py-5">
          <Package size={36} className="mb-2 opacity-50" />
          <p className="mb-2">{emptyMsg}</p>
          {tab === 'open' && (
            <Link to="/wholesale/catalog" className="btn btn-sm btn-primary">
              Browse Catalog
            </Link>
          )}
        </div>
      );
    }

    return (
      <div className="table-responsive">
        <table className="table table-hover table-striped mb-0">
          <thead className="table-light">
            <tr>
              <th>Order #</th>
              <th>Date</th>
              <th>Items</th>
              <th>Total</th>
              <th>Status</th>
              <th>Due Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(o => {
              const isPastDue =
                o.invoiceDueDate && new Date(o.invoiceDueDate) < new Date() && o.orderStatus === 'invoiced';
              return (
                <tr key={o.orderID}>
                  <td className="fw-semibold">
                    <Link to={`/wholesale/orders/${o.orderID}`}>#{o.orderID}</Link>
                  </td>
                  <td>{new Date(o.orderDate).toLocaleDateString('en-US')}</td>
                  <td>{o.itemCount ?? '—'}</td>
                  <td>{fmt(o.totalAmount)}</td>
                  <td>
                    <span className={`badge ${STATUS_BADGE[o.orderStatus] || 'bg-secondary'}`}>
                      {o.orderStatus}
                    </span>
                  </td>
                  <td>
                    {o.invoiceDueDate ? (
                      <span className={isPastDue ? 'text-danger fw-semibold' : ''}>
                        {new Date(o.invoiceDueDate).toLocaleDateString('en-US')}
                        {isPastDue && ' (Past Due)'}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <div className="d-flex gap-1 align-items-center">
                      <Link
                        to={`/wholesale/orders/${o.orderID}`}
                        className="btn btn-sm btn-outline-primary"
                      >
                        View
                      </Link>
                      <button
                        className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
                        onClick={() => setReorderOrderId(o.orderID)}
                        title="Review and reorder items"
                      >
                        <RotateCcw size={12} /> Reorder
                      </button>
                      {['invoiced', 'paid', 'delivered'].includes(o.orderStatus) && (
                        <button
                          className="btn btn-sm btn-outline-dark d-inline-flex align-items-center"
                          onClick={() => handleDownload(o.orderID)}
                          disabled={dlLoading === o.orderID}
                          title="Download invoice PDF"
                        >
                          {dlLoading === o.orderID
                            ? <span className="spinner-border spinner-border-sm" />
                            : <Download size={12} />}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div>
      {toast && (
        <div className="position-fixed top-0 end-0 p-3" style={{ zIndex: 1100 }}>
          <div className="toast show align-items-center text-bg-success border-0">
            <div className="d-flex">
              <div className="toast-body">{toast}</div>
              <button type="button" className="btn-close btn-close-white me-2 m-auto" onClick={() => setToast('')} />
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="py-4 px-4 bg-white border-bottom d-flex align-items-center gap-2">
        <ClipboardList size={20} />
        <h5 className="mb-0 fw-bold">My Orders</h5>
      </div>

      <div className="container-fluid py-4 px-4">
        {error && <div className="alert alert-danger mb-4">{error}</div>}

        {/* Tabs */}
        <ul className="nav nav-tabs mb-4">
          <li className="nav-item">
            <button
              className={`nav-link ${tab === 'open' ? 'active' : ''}`}
              onClick={() => setTab('open')}
            >
              Open
              {openOrders.length > 0 && (
                <span className="badge bg-primary ms-2">{openOrders.length}</span>
              )}
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${tab === 'completed' ? 'active' : ''}`}
              onClick={() => setTab('completed')}
            >
              Completed
              {completedOrders.length > 0 && (
                <span className="badge bg-secondary ms-2">{completedOrders.length}</span>
              )}
            </button>
          </li>
        </ul>

        <div className="card shadow-sm">
          {renderTable(
            displayed,
            tab === 'open' ? 'No open orders.' : 'No completed orders.'
          )}
        </div>
      </div>

      <ReorderModal
        orderId={reorderOrderId}
        onClose={() => setReorderOrderId(null)}
        onSuccess={(count) => showToast(`${count} item${count !== 1 ? 's' : ''} added to cart.`)}
      />
    </div>
  );
}
